/**
 * Manufacturing API Client
 * BOM management + Production Order tracking
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type BomType = 'ASSEMBLY' | 'KIT' | 'REFURBISHMENT' | 'CUSTOM';
export type BomStatus = 'ACTIVE' | 'INACTIVE';

export type ProductionOrderStatus = 'DRAFT' | 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export type ProductionPhaseType = 'PREPARATION' | 'MANUFACTURING' | 'QC' | 'RECEIPT';
export type ProductionPhaseStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
export type QualityStatus = 'PENDING' | 'PASSED' | 'FAILED' | 'PARTIAL';

export interface BomLine {
  id: string;
  bomId: string;
  productId: string;
  productName: string;
  productSku?: string;
  quantity: number;
  uom: string;
  scrapRate: number;
  optional: boolean;
  sortOrder: number;
  parentLineId?: string;
  children?: BomLine[];
}

export interface Bom {
  id: string;
  bomCode: string;
  description?: string;
  bomType: BomType;
  status: BomStatus;
  outputProductId: string;
  outputProductName: string;
  outputQuantity: number;
  version: number;
  validFrom?: string;
  validUntil?: string;
  lines: BomLine[];
  lineCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BomListResponse {
  data: Bom[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface BomResponse {
  data: Bom;
}

export interface BomFilters {
  bomType?: BomType;
  status?: BomStatus;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateBomDTO {
  bomCode: string;
  description?: string;
  bomType: BomType;
  outputProductId: string;
  outputQuantity?: number;
  validFrom?: string;
  validUntil?: string;
}

export interface UpdateBomDTO {
  description?: string;
  bomType?: BomType;
  status?: BomStatus;
  outputQuantity?: number;
  validFrom?: string;
  validUntil?: string;
}

export interface AddBomLineDTO {
  productId: string;
  quantity: number;
  uom?: string;
  scrapRate?: number;
  optional?: boolean;
  sortOrder?: number;
  parentLineId?: string;
}

export interface ProductionPhase {
  id: string;
  productionOrderId: string;
  phaseType: ProductionPhaseType;
  status: ProductionPhaseStatus;
  assigneeId?: string;
  assigneeName?: string;
  startedAt?: string;
  completedAt?: string;
  durationMinutes?: number;
  scrapQuantity: number;
  scrapReason?: string;
  sortOrder: number;
}

export interface LotBatch {
  id: string;
  productionOrderId: string;
  lotNumber: string;
  quantity: number;
  qualityStatus: QualityStatus;
  producedAt: string;
  notes?: string;
}

export interface ProductionOrder {
  id: string;
  orderNumber: string;
  bomId: string;
  bomCode: string;
  outputProductId: string;
  outputProductName: string;
  status: ProductionOrderStatus;
  plannedQuantity: number;
  actualQuantity: number;
  plannedStart?: string;
  plannedEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  phases: ProductionPhase[];
  lots: LotBatch[];
  completedPhases: number;
  totalPhases: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductionOrderListResponse {
  data: ProductionOrder[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface ProductionOrderResponse {
  data: ProductionOrder;
}

export interface ProductionOrderFilters {
  status?: ProductionOrderStatus;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateProductionOrderDTO {
  bomId: string;
  plannedQuantity: number;
  plannedStart?: string;
  plannedEnd?: string;
}

export interface RecordScrapDTO {
  phaseId: string;
  quantity: number;
  reason?: string;
}

// ============================================
// API FUNCTIONS — BOM
// ============================================

/**
 * Get list of BOMs with filters
 */
export async function getBoms(filters: BomFilters = {}): Promise<BomListResponse> {
  const params = new URLSearchParams();

  if (filters.bomType) params.append('bomType', filters.bomType);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/manufacturing/bom?${queryString}` : '/manufacturing/bom';

  return api.get<BomListResponse>(endpoint);
}

/**
 * Get single BOM by ID
 */
export async function getBomById(id: string): Promise<BomResponse> {
  return api.get<BomResponse>(`/manufacturing/bom/${id}`);
}

/**
 * Create new BOM
 */
export async function createBom(data: CreateBomDTO): Promise<BomResponse> {
  return api.post<BomResponse>('/manufacturing/bom', data);
}

/**
 * Update BOM
 */
export async function updateBom(id: string, data: UpdateBomDTO): Promise<BomResponse> {
  return api.patch<BomResponse>(`/manufacturing/bom/${id}`, data);
}

/**
 * Delete BOM
 */
export async function deleteBom(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/manufacturing/bom/${id}`);
}

/**
 * Add line to BOM
 */
export async function addBomLine(bomId: string, data: AddBomLineDTO): Promise<{ data: BomLine }> {
  return api.post<{ data: BomLine }>(`/manufacturing/bom/${bomId}/lines`, data);
}

/**
 * Remove line from BOM
 */
export async function removeBomLine(bomId: string, lineId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/manufacturing/bom/${bomId}/lines/${lineId}`);
}

// ============================================
// API FUNCTIONS — Production Orders
// ============================================

/**
 * Get list of production orders with filters
 */
export async function getProductionOrders(
  filters: ProductionOrderFilters = {}
): Promise<ProductionOrderListResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/manufacturing/orders?${queryString}` : '/manufacturing/orders';

  return api.get<ProductionOrderListResponse>(endpoint);
}

/**
 * Get single production order by ID
 */
export async function getProductionOrderById(id: string): Promise<ProductionOrderResponse> {
  return api.get<ProductionOrderResponse>(`/manufacturing/orders/${id}`);
}

/**
 * Create new production order
 */
export async function createProductionOrder(
  data: CreateProductionOrderDTO
): Promise<ProductionOrderResponse> {
  return api.post<ProductionOrderResponse>('/manufacturing/orders', data);
}

/**
 * Update production order status
 */
export async function updateProductionOrderStatus(
  id: string,
  status: ProductionOrderStatus
): Promise<ProductionOrderResponse> {
  return api.patch<ProductionOrderResponse>(`/manufacturing/orders/${id}/status`, {
    status,
  });
}

/**
 * Start a production phase
 */
export async function startPhase(
  orderId: string,
  phaseId: string
): Promise<{ data: ProductionPhase }> {
  return api.post<{ data: ProductionPhase }>(
    `/manufacturing/orders/${orderId}/phases/${phaseId}/start`
  );
}

/**
 * Complete a production phase
 */
export async function completePhase(
  orderId: string,
  phaseId: string
): Promise<{ data: ProductionPhase }> {
  return api.post<{ data: ProductionPhase }>(
    `/manufacturing/orders/${orderId}/phases/${phaseId}/complete`
  );
}

/**
 * Record scrap for a production order
 */
export async function recordScrap(
  orderId: string,
  data: RecordScrapDTO
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/manufacturing/orders/${orderId}/scrap`, data);
}
